
import React from 'react';
import { CircleHelp } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import { useMobileHeaderLogo } from '@/hooks/useOfflineLogo';

interface MobileHeaderProps {
  title?: string;
  helpContent?: {
    title: string;
    description: string;
    content: React.ReactNode;
  };
}

export function MobileHeader({ title = 'The Mat Monster', helpContent }: MobileHeaderProps) {
  const [helpOpen, setHelpOpen] = React.useState(false);
  const { logoUrl, isFromCache, isLoading } = useMobileHeaderLogo();

  return (
  <div
    className="fixed top-0 left-0 right-0 z-50 bg-background shadow-sm border-b border-border"
    style={{
      paddingTop: 'env(safe-area-inset-top)',
      height: 'calc(3.5rem + env(safe-area-inset-top))',
    }}
  >
      <div className="flex h-14 items-center justify-between px-4">
        <h1 className="text-base font-bold tracking-tight">{title}</h1>

        <div className="flex items-center gap-2">
          {helpContent && (
            <CircleHelp
              className="h-6 w-6 text-primary cursor-pointer hover:text-primary/80 transition-colors"
              onClick={() => setHelpOpen(true)}
              strokeWidth={2}
            />
          )}
          {isLoading ? (
            <div className="h-16 w-16 flex items-center justify-center transform -translate-y-1">
              <div className="animate-pulse bg-gray-200 h-12 w-12 rounded"></div>
            </div>
          ) : (
            <img
              src={logoUrl || '/lovable-uploads/b9e6fa8f-f749-4515-b67c-d28715d804c5.png'}
              alt="The Mat Monster"
              className="h-16 w-auto transform -translate-y-1"
              title={isFromCache ? 'Cached logo (offline ready)' : 'Online logo'}
              style={{ visibility: 'visible' }}
            />
          )}
        </div>
      </div>

      {/* Help dialog */}
      {helpContent && (
        <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{helpContent.title}</DialogTitle>
              <DialogDescription>{helpContent.description}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">{helpContent.content}</div>
            <div className="flex justify-end">
              <DialogClose asChild>
                <Button variant="default" size="sm">
                  Got it
                </Button>
              </DialogClose>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
